import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, AlertTriangle, Info } from "lucide-react";
import {
  useCancelBooking, useNoShowBooking, useRescheduleBooking,
  getListMemberChargesQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface Props {
  chargeId: number;
  action: "cancel" | "no_show" | "reschedule";
  open: boolean;
  onClose: () => void;
  /** Optional charge info for display in dialog body */
  chargeDescription?: string;
  chargeDate?: string;
  chargeAmount?: number;
  memberId?: number;
}

export function CancelBookingDialog({
  chargeId,
  action,
  open,
  onClose,
  chargeDescription,
  chargeDate,
  chargeAmount,
  memberId,
}: Props) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const [newDate, setNewDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const cancelMutation = useCancelBooking();
  const noShowMutation = useNoShowBooking();
  const rescheduleMutation = useRescheduleBooking();

  const isPending =
    cancelMutation.isPending || noShowMutation.isPending || rescheduleMutation.isPending;

  const reasonOk = reason.trim().length >= 5;
  const rescheduleOk = action !== "reschedule" || (newDate.length > 0 && startTime.length > 0);
  const submitOk = reasonOk && rescheduleOk && !isPending;

  function invalidate() {
    qc.invalidateQueries({ queryKey: getListMemberChargesQueryKey(memberId ? { memberId } : undefined) });
  }

  async function onSubmit() {
    try {
      if (action === "cancel") {
        await cancelMutation.mutateAsync({ id: chargeId, data: { reason: reason.trim() } });
        toast.success("Booking cancelled.");
      } else if (action === "no_show") {
        await noShowMutation.mutateAsync({ id: chargeId, data: { reason: reason.trim() } });
        toast.success("No-show recorded.");
      } else if (action === "reschedule") {
        const formattedDate = newDate
          ? new Date(newDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
          : "—";
        await rescheduleMutation.mutateAsync({
          id: chargeId,
          data: {
            reason: reason.trim(),
            transactionDate: newDate || undefined,
            bookingStartTime: startTime,
            bookingEndTime: endTime || null,
          },
        });
        toast.success(`Booking rescheduled to ${formattedDate}.`);
      }
      invalidate();
      onClose();
    } catch (err) {
      const msg = (err as Error)?.message ?? "Unknown error";
      toast.error(`Action failed: ${msg}`);
    }
  }

  const chargeInfo = chargeDescription || chargeDate || chargeAmount != null;

  if (action === "cancel") {
    return (
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel Booking</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {chargeInfo && (
              <div className="rounded-md bg-muted p-3 text-sm space-y-1">
                {chargeDescription && <div>{chargeDescription}</div>}
                {chargeDate && <div className="text-muted-foreground">{chargeDate}</div>}
                {chargeAmount != null && <div className="font-mono">₹{chargeAmount.toFixed(2)}</div>}
              </div>
            )}
            <Alert className="border-amber-200 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-700">
                If already paid, a refund will be flagged for finance.
              </AlertDescription>
            </Alert>
            <div>
              <Label>Reason for cancellation (required)</Label>
              <Textarea
                placeholder="Reason for cancellation (required)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                minLength={5}
                className="mt-1"
              />
              {reason.length > 0 && !reasonOk && (
                <p className="text-xs text-destructive mt-1">Reason must be at least 5 characters.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Discard</Button>
            <Button variant="destructive" disabled={!submitOk} onClick={onSubmit}>
              {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Cancel Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (action === "no_show") {
    return (
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record No-Show</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {chargeInfo && (
              <div className="rounded-md bg-muted p-3 text-sm space-y-1">
                {chargeDescription && <div>{chargeDescription}</div>}
                {chargeDate && <div className="text-muted-foreground">{chargeDate}</div>}
                {chargeAmount != null && <div className="font-mono">₹{chargeAmount.toFixed(2)}</div>}
              </div>
            )}
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Charge stands per business policy. Member is still billed.
              </AlertDescription>
            </Alert>
            <div>
              <Label>No-show note (required)</Label>
              <Textarea
                placeholder="No-show note (required)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                minLength={5}
                className="mt-1"
              />
              {reason.length > 0 && !reasonOk && (
                <p className="text-xs text-destructive mt-1">Note must be at least 5 characters.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Discard</Button>
            <Button variant="default" disabled={!submitOk} onClick={onSubmit}>
              {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Record No-Show
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Reschedule
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reschedule Booking</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {chargeInfo && (
            <div className="rounded-md bg-muted p-3 text-sm space-y-1">
              {chargeDescription && <div>{chargeDescription}</div>}
              {chargeDate && <div className="text-muted-foreground">{chargeDate}</div>}
            </div>
          )}
          <div>
            <Label>New booking date</Label>
            <Input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <Label>Start</Label>
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="flex-1">
              <Label>End</Label>
              <Input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <Label>Reschedule reason (required)</Label>
            <Textarea
              placeholder="Reschedule reason (required)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              minLength={5}
              className="mt-1"
            />
            {reason.length > 0 && !reasonOk && (
              <p className="text-xs text-destructive mt-1">Reason must be at least 5 characters.</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Discard</Button>
          <Button variant="default" disabled={!submitOk} onClick={onSubmit}>
            {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Confirm Reschedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
